import { MutationCache, QueryClient } from "@tanstack/react-query";

/** Résumé patient (`/patients/{id}/summary/`) : compteurs des onglets de la
 *  fiche, solde et parcours. */
const PATIENT_SUMMARY_KEY = ["patients", "summary"] as const;

// Le résumé agrège des actes détenus par d'AUTRES features (consultations,
// ordonnances, rendez-vous, ventes, paiements) : aucune de leurs mutations ne
// l'invalidait, chacune ne périmant que sa propre liste. Le compteur d'onglet
// restait donc figé sur la valeur d'avant l'écriture — « Consultations (1) »
// au-dessus d'un tableau vide après suppression de la seule consultation.
// Invalidation centrale plutôt que dans la vingtaine de hooks de mutation
// concernés : une écriture réussie, quelle qu'en soit l'origine, périme le
// résumé. Le refetch ne part que si la fiche patient est montée.
const mutationCache = new MutationCache({
  onSuccess: () => {
    void queryClient.invalidateQueries({ queryKey: PATIENT_SUMMARY_KEY });
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Cache conservé toute la session (offline-first) : une fiche déjà
      // consultée reste lisible après une coupure réseau, même si le composant
      // a été démonté entre-temps. Mémoire seule — rien n'est persisté sur
      // disque (choix RGPD, poste clinique partagé).
      gcTime: 24 * 60 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      // Hors ligne : servir immédiatement le cache au lieu de rester en pause.
      networkMode: "offlineFirst",
    },
    mutations: {
      retry: 0,
      // "always" : une mutation ORDINAIRE (édition, suppression) doit ÉCHOUER
      // visiblement hors ligne, pas rester en pause. Avec "online", TanStack
      // Query met la mutation en pause et sa promesse ne se résout jamais :
      // l'écran d'édition restait figé sur son spinner, sans erreur ni sortie.
      //
      // La file d'écritures offline n'est PAS concernée : les trois créations
      // rejouables (patients / rendez-vous / consultations) redéclarent
      // `networkMode: "online"` dans leurs propres défauts de mutation, qui
      // priment sur celui-ci — voir lib/api/offline-mutations et offline-registry.
      networkMode: "always",
    },
  },
});
